import * as React from "react";
import { cn } from "./utils";

export interface TooltipProps {
  label: React.ReactNode;
  delay?: number;
  children: React.ReactElement;
  className?: string;
  side?: "top" | "bottom" | "left" | "right";
}

export const Tooltip = ({
  label,
  delay = 300,
  children,
  className,
  side = "bottom",
}: TooltipProps) => {
  const [open, setOpen] = React.useState(false);
  const id = React.useId();
  const timer = React.useRef<number | null>(null);
  const originalRef = (children as unknown as { ref?: React.Ref<HTMLElement> })
    .ref;

  const show = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), delay);
  };

  const hide = () => {
    if (timer.current) window.clearTimeout(timer.current);
    setOpen(false);
  };

  React.useEffect(() => {
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const sideClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 -translate-y-2",
    bottom: "top-full left-1/2 -translate-x-1/2 translate-y-2",
    left: "right-full top-1/2 -translate-y-1/2 -translate-x-2",
    right: "left-full top-1/2 -translate-y-1/2 translate-x-3",
  };

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {React.cloneElement(children, {
        ref: (node: HTMLElement) => {
          if (typeof originalRef === "function") originalRef(node);
          else if (originalRef && typeof originalRef === "object") {
            (
              originalRef as React.MutableRefObject<HTMLElement | null>
            ).current = node;
          }
        },
        "aria-describedby": open ? id : undefined,
      })}
      {open ? (
        <span
          role="tooltip"
          id={id}
          className={cn(
            "pointer-events-none absolute z-50 w-max max-w-[28rem] whitespace-normal rounded-md border border-border bg-surface px-2 py-1 text-xs text-foreground shadow-md transition-opacity duration-200",
            sideClasses[side],
            className,
          )}
        >
          {label}
        </span>
      ) : null}
    </span>
  );
};
Tooltip.displayName = "Tooltip";
